import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import {
  Building2,
  Save,
  Upload,
  Trash2,
  Instagram,
  Facebook,
  Globe,
} from "lucide-react";
import { IMaskInput } from 'react-imask';
import useAutoRefresh from "../hooks/useAutoRefresh";
import { buildUploadUrl } from "../utils/runtimeConfig";
import FilaZeroTimeSelect from "../components/FilaZeroTimeSelect";
import FilaZeroSelect from "../components/FilaZeroSelect";

const estadoOptions = [
  ["AC", "Acre"], ["AL", "Alagoas"], ["AP", "Amapá"], ["AM", "Amazonas"],
  ["BA", "Bahia"], ["CE", "Ceará"], ["DF", "Distrito Federal"],
  ["ES", "Espírito Santo"], ["GO", "Goiás"], ["MA", "Maranhão"],
  ["MT", "Mato Grosso"], ["MS", "Mato Grosso do Sul"], ["MG", "Minas Gerais"],
  ["PA", "Pará"], ["PB", "Paraíba"], ["PR", "Paraná"], ["PE", "Pernambuco"],
  ["PI", "Piauí"], ["RJ", "Rio de Janeiro"], ["RN", "Rio Grande do Norte"],
  ["RS", "Rio Grande do Sul"], ["RO", "Rondônia"], ["RR", "Roraima"],
  ["SC", "Santa Catarina"], ["SP", "São Paulo"], ["SE", "Sergipe"],
  ["TO", "Tocantins"],
].map(([value, label]) => ({ value, label: `${value} — ${label}` }));

const Estabelecimentos = () => {
  const [estabelecimento, setEstabelecimento] = useState(null);
  const [estabelecimentos, setEstabelecimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [erros, setErros] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const estabelecimentoRef = useRef(null);

  // Spinner
  const Spinner = ({ size = 16 }) => (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        border: "2px solid rgba(0,0,0,0.15)",
        borderTopColor: "var(--primary)",
        borderRadius: "50%",
        animation: "spin 0.6s linear infinite",
      }}
    />
  );

  // CSS keyframes
  useEffect(() => {
    if (document.getElementById("estabelecimento-styles")) return;
    const style = document.createElement("style");
    style.id = "estabelecimento-styles";
    style.innerHTML = `
      @keyframes spin { to { transform: rotate(360deg); } }
      .estabelecimento-container {
        padding: 32px;
        background: var(--bg);
      }
      .header-section {
        margin-bottom: 32px;
      }
      .header-title {
        font-size: 28px;
        font-weight: 800;
        color: var(--text);
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 8px;
      }
      .header-subtitle {
        font-size: 14px;
        color: var(--muted);
        margin-left: 40px;
        word-break: break-word;
      }
      .content-wrapper {
        display: grid;
        grid-template-columns: 380px 1fr;
        gap: 32px;
      }
      .image-section {
        background: var(--card);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        height: fit-content;
      }
      .image-preview {
        width: 100%;
        height: 280px;
        border-radius: 12px;
        background: var(--bg);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        margin-bottom: 16px;
        border: 1px solid var(--line);
      }
      .image-preview img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .no-image-text {
        color: var(--muted);
        font-size: 13px;
      }
      .form-section {
        background: var(--card);
        border-radius: 16px;
        padding: 24px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        margin-bottom: 20px;
      }
      .section-title {
        font-size: 15px;
        font-weight: 700;
        color: var(--text);
        margin-bottom: 18px;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .form-grid-2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
      }
      .form-grid-3 {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 16px;
      }
      .form-grid-1 {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
      }
      .form-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .field-label {
        font-weight: 600;
        font-size: 13px;
        color: var(--text);
      }
      .field {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid var(--line);
        border-radius: 8px;
        background: var(--bg);
        color: var(--text);
        font-size: 14px;
        transition: all 0.2s;
      }
      .field:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
      }
      .field-textarea {
        resize: vertical;
        min-height: 100px;
        font-family: inherit;
      }
      .input-error {
        border-color: #ef4444 !important;
        box-shadow: 0 0 0 3px rgba(239,68,68,0.1) !important;
      }
      .error-text {
        color: #ef4444;
        font-size: 12px;
      }
      .btn-upload {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 10px 14px;
        background: var(--primary);
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13px;
        transition: all 0.2s;
        margin-bottom: 8px;
      }
      .btn-upload:hover {
        opacity: 0.9;
        transform: translateY(-1px);
      }
      .btn-remove {
        width: 100%;
        padding: 8px 12px;
        background: #fee2e2;
        color: #991b1b;
        border: 1px solid #fecaca;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
        font-size: 12px;
        transition: all 0.2s;
      }
      .btn-remove:hover {
        background: #fecaca;
      }
      .file-input {
        display: none;
      }
      .icon-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .icon-input-wrapper svg {
        position: absolute;
        left: 12px;
        color: var(--muted);
        pointer-events: none;
      }
      .icon-input {
        padding-left: 40px !important;
      }
      .save-button-wrapper {
        position: fixed;
        bottom: 32px;
        right: 32px;
        z-index: 100;
      }
      .estabelecimento-save-bar {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        padding: 10px;
        background: rgba(255,255,255,0.92);
        border: 1px solid var(--line);
        border-radius: 14px;
        box-shadow: 0 18px 40px rgba(15,23,42,0.14);
        backdrop-filter: blur(10px);
      }
      .estabelecimento-save-bar__status {
        color: var(--muted);
        font-size: 13px;
        font-weight: 700;
        white-space: nowrap;
        padding-left: 4px;
      }
      .save-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px 24px;
        background: var(--primary);
        color: white;
        border: none;
        border-radius: 10px;
        cursor: pointer;
        font-weight: 700;
        font-size: 15px;
        box-shadow: 0 8px 24px rgba(37,99,235,0.3);
        transition: all 0.3s;
      }
      .save-button:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba(37,99,235,0.4);
      }
      .save-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      @media (max-width: 1024px) {
        .content-wrapper {
          grid-template-columns: 1fr;
        }
      }
      @media (max-width: 768px) {
        .estabelecimento-container {
          padding: 16px;
        }
        .header-title {
          font-size: 22px;
        }
        .header-subtitle {
          margin-left: 0;
        }
        .image-section,
        .form-section {
          padding: 16px;
        }
        .image-preview {
          height: 220px;
        }
        .form-grid-2 {
          grid-template-columns: 1fr;
        }
        .form-grid-3 {
          grid-template-columns: 1fr;
        }
        .save-button-wrapper {
          bottom: 12px;
          right: 16px;
          left: 16px;
        }
        .estabelecimento-save-bar {
          justify-content: space-between;
          width: auto;
        }
        .estabelecimento-save-bar__status {
          white-space: normal;
        }
        .save-button {
          flex: 1;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.body.classList.contains("dark");
      setTheme(isDark ? "dark" : "light");
    });
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const carregarTipos = async () => {
      setLoadingTipos(true);
      try {
        const res = await api.get('/tipos-estabelecimento');
        const lista = Array.isArray(res.data) ? res.data : (res.data?.resultados || []);
        setTipos(lista);
      } catch (err) {
        console.error('Erro ao carregar tipos:', err);
        toast.error('Erro ao carregar tipos de estabelecimento.');
      } finally {
        setLoadingTipos(false);
      }
    };
    carregarTipos();
  }, []);

  const carregarEstabelecimentos = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/estabelecimentos');
      const lista = Array.isArray(data) ? data : (data.resultados || []);
      setEstabelecimentos(lista);

      // Se não há estabelecimento selecionado, use o primeiro da lista
      if (!estabelecimentoRef.current && lista.length > 0) {
        setEstabelecimento(lista[0]);
        try {
          localStorage.setItem('estabelecimento_id', String(lista[0].id));
          try {
            window.dispatchEvent(new CustomEvent('fz:estabelecimento:changed', { detail: { id: String(lista[0].id) } }));
          } catch (e) {}
        } catch(e){}
      }
      
      return lista;
    } catch (err) {
      console.error('Erro ao carregar estabelecimentos:', err);
      if (!silent) toast.error('Erro ao carregar estabelecimentos!');
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarEstabelecimentos(false);
  }, [carregarEstabelecimentos]);

  // Bairro vem do ViaCEP (não há coluna no banco) — completa ao abrir/editar pelo CEP.
  useEffect(() => {
    const cep = String(estabelecimento?.cep || "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    if (estabelecimento?.bairro) return;
    let cancelled = false;
    (async () => {
      const endereco = await buscarEnderecoPorCep(cep);
      if (cancelled || !endereco?.bairro) return;
      setEstabelecimento((prev) => {
        if (String(prev?.cep || "").replace(/\D/g, "") !== cep) return prev;
        if (prev?.bairro) return prev;
        return { ...prev, bairro: endereco.bairro };
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [estabelecimento?.cep, estabelecimento?.bairro]);

  useEffect(() => {
    const handler = () => carregarEstabelecimentos(true);
    window.addEventListener("fz:agendamento", handler);
    window.addEventListener("fz:notificacao", handler);
    return () => {
      window.removeEventListener("fz:agendamento", handler);
      window.removeEventListener("fz:notificacao", handler);
    };
  }, [carregarEstabelecimentos]);

  useEffect(() => {
    estabelecimentoRef.current = estabelecimento;
  }, [estabelecimento]);

  useAutoRefresh(
    () => carregarEstabelecimentos(true),
    15000,
    !saving
  );

  

  const handleInputChange = (field, value) => {
    setEstabelecimento(prev => ({
      ...prev,
      [field]: value
    }));
    setIsEditing(true);
  };

  const buscarEnderecoPorCep = async (cep) => {
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep.replace(/\D/g, '')}/json/`);
      if (!response.ok) return null;
      const data = await response.json();
      if (data.erro) return null;
      return data;
    } catch {
      return null;
    }
  };

  const handleCepBlur = async (e) => {
    const cep = e.target.value;
    if (cep && cep.replace(/\D/g, '').length === 8) {
      const endereco = await buscarEnderecoPorCep(cep);
      if (endereco) {
        setEstabelecimento(prev => ({
          ...prev,
          endereco: endereco.logradouro || '',
          bairro: endereco.bairro || '',
          cidade: endereco.localidade || '',
          estado: endereco.uf || '',
        }));
        setIsEditing(true);
      }
    }
  };

  const handleFotoChange = (e, field) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setEstabelecimento(prev => ({
          ...prev,
          [field]: file,
          [`${field}_preview`]: event.target.result,
          [`${field}_remove`]: false
        }));
        setIsEditing(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const removerFoto = (field) => {
    setEstabelecimento(prev => ({
      ...prev,
      [field]: null,
      [`${field}_preview`]: null,
      [`${field}_remove`]: true
    }));
    setIsEditing(true);
  };

  const validar = () => {
    const e = {};
    const emailRegex = /^\S+@\S+\.\S+$/;
    const telefoneRegex = /^[0-9+\s()-]{6,20}$/;
    const cepRegex = /^\d{5}-?\d{3}$/;

    if (!estabelecimento.nome || String(estabelecimento.nome).trim() === '') {
      e.nome = "Digite o nome do estabelecimento";
    }

    if (estabelecimento.email && !emailRegex.test(estabelecimento.email)) {
      e.email = "E-mail inválido";
    }

    if (estabelecimento.telefone && !telefoneRegex.test(estabelecimento.telefone)) {
      e.telefone = "Telefone inválido";
    }

    const docDigits = String(estabelecimento.documento_faturamento || "").replace(/\D/g, "");
    if (!docDigits) {
      e.documento_faturamento = "Informe o CPF ou CNPJ de cobrança";
    } else if (docDigits.length !== 11 && docDigits.length !== 14) {
      e.documento_faturamento = "CPF ou CNPJ inválido";
    }

    if (estabelecimento.cep && !cepRegex.test(estabelecimento.cep)) {
      e.cep = "CEP inválido";
    }

    if (estabelecimento.hora_abertura && estabelecimento.hora_fechamento) {
      const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
      const hA = timeRegex.exec(estabelecimento.hora_abertura);
      const hF = timeRegex.exec(estabelecimento.hora_fechamento);
      if (!hA || !hF) {
        e.horarios = 'Formato de horário inválido';
      } else {
        const minA = Number(hA[1]) * 60 + Number(hA[2]);
        const minF = Number(hF[1]) * 60 + Number(hF[2]);
        if (minA >= minF) e.horarios = 'Hora de abertura deve ser anterior à de fechamento';
      }
    }

    // Verifica duplicidade local (nome + cidade) — evita chamada caso o usuário já tenha o mesmo registro
    if (estabelecimento.nome && estabelecimento.cidade) {
      const dup = estabelecimentos.find(ei => ei.nome === estabelecimento.nome && ei.cidade === estabelecimento.cidade && String(ei.id) !== String(estabelecimento.id));
      if (dup) {
        e.duplicado = 'Já existe um estabelecimento com esse nome nesta cidade';
      }
    }

    setErros(e);
    return Object.keys(e).length === 0;
  };

  const salvar = async () => {
    if (!validar()) return;
    setSaving(true);
    try {
      // Checagem remota de duplicidade antes de enviar
      try {
        const dupResp = await api.post('/estabelecimentos/verificar-duplicidade', {
          nome: estabelecimento.nome,
          cidade: estabelecimento.cidade,
          id: estabelecimento.id,
        });
        if (dupResp && dupResp.data && dupResp.data.duplicado) {
          setErros(prev => ({ ...prev, duplicado: 'Já existe um estabelecimento com esse nome nesta cidade' }));
          setSaving(false);
          return;
        }
      } catch (checkErr) {
        // se falhar a checagem remota, não bloqueia o salvamento — apenas loga
        console.error('Erro na checagem remota de duplicidade:', checkErr);
      }
      const formData = new FormData();
      formData.append('nome', estabelecimento.nome);
      const tipoId = estabelecimento.tipo_id;
      if (tipoId !== undefined && tipoId !== null && String(tipoId) !== '' && String(tipoId) !== '0') {
        formData.append('tipo_id', String(tipoId));
      }
      formData.append('descricao', estabelecimento.descricao || '');
      formData.append('endereco', estabelecimento.endereco || '');
      formData.append('numero', estabelecimento.numero || '');
      formData.append('cidade', estabelecimento.cidade || '');
      formData.append('estado', estabelecimento.estado || '');
      formData.append('cep', estabelecimento.cep || '');
      formData.append('telefone', estabelecimento.telefone || '');
      formData.append('email', estabelecimento.email || '');
      formData.append(
        'documento_faturamento',
        String(estabelecimento.documento_faturamento || '').replace(/\D/g, '')
      );
      formData.append('instagram', estabelecimento.instagram || '');
      formData.append('facebook', estabelecimento.facebook || '');
      formData.append('whatsapp', estabelecimento.whatsapp || '');
      formData.append('site', estabelecimento.site || '');
      formData.append('hora_abertura', estabelecimento.hora_abertura || '');
      formData.append('hora_fechamento', estabelecimento.hora_fechamento || '');

      if (estabelecimento.imagem_logo instanceof File) {
        formData.append('imagem_logo', estabelecimento.imagem_logo);
      } else if (estabelecimento.imagem_logo_remove) {
        formData.append('imagem_logo_remove', 'true');
      }
      if (estabelecimento.foto_1 instanceof File) {
        formData.append('foto_1', estabelecimento.foto_1);
      } else if (estabelecimento.foto_1_remove) {
        formData.append('foto_1_remove', 'true');
      }
      if (estabelecimento.foto_2 instanceof File) {
        formData.append('foto_2', estabelecimento.foto_2);
      } else if (estabelecimento.foto_2_remove) {
        formData.append('foto_2_remove', 'true');
      }
      if (estabelecimento.foto_3 instanceof File) {
        formData.append('foto_3', estabelecimento.foto_3);
      } else if (estabelecimento.foto_3_remove) {
        formData.append('foto_3_remove', 'true');
      }

      const { data } = await api.put(`/estabelecimentos/${estabelecimento.id}`, formData);
      if (data && data.estabelecimento) {
        const updated = data.estabelecimento;
        setEstabelecimento(updated);
        setEstabelecimentos(prev => prev.map(e => (e.id === updated.id ? updated : e)));
        try {
          localStorage.setItem('estabelecimento_id', String(updated.id));
          try {
            window.dispatchEvent(new CustomEvent('fz:estabelecimento:changed', { detail: { id: String(updated.id) } }));
          } catch (e) {}
        } catch {}

        // Force reload from server to ensure full sync
        const lista = await carregarEstabelecimentos();
        const refreshed = Array.isArray(lista) ? lista.find(i => i.id === updated.id) : null;
        if (refreshed) setEstabelecimento(refreshed);
      }
      // limpar erros e exibir sucesso inline
      setErros({});
      toast.success('Estabelecimento atualizado com sucesso!');
      setIsEditing(false);
    } catch (err) {
      console.error('Erro ao salvar estabelecimento:', err);
      toast.error('Erro ao salvar estabelecimento!');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <ToastContainer position="top-right" theme={theme === "dark" ? "dark" : "colored"} />
        <p style={{ display: "flex", alignItems: "center", gap: 8, color: 'var(--muted)' }}>
          <Spinner /> Carregando estabelecimento...
        </p>
      </div>
    );
  }

  if (!estabelecimento) {
    return (
      <div style={{ padding: '32px', color: 'var(--muted)' }}>
        <ToastContainer position="top-right" theme={theme === "dark" ? "dark" : "colored"} />
        <div className="empty-state empty-state--compact">
          <Building2 size={34} />
          <strong>Nenhum estabelecimento disponível</strong>
          <span>Não encontramos um estabelecimento vinculado ao seu usuário.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="estabelecimento-container">
      <ToastContainer position="top-right" theme={theme === "dark" ? "dark" : "colored"} />

      {/* Header */}
      <div className="header-section">
        <div className="header-title">
          <Building2 size={32} style={{ color: 'var(--primary)' }} />
          Dados do Estabelecimento
        </div>
        <div className="header-subtitle">
          Configure identidade, contato, localização e operação de {estabelecimento.nome}.
        </div>
      </div>

        {erros.form && (
          <div style={{ marginBottom: 12 }} className="error-text">{erros.form}</div>
        )}

      {/* Seletor removido: o estabelecimento ativo é determinado pelo login/token */}

      {/* Main Content */}
      <div className="content-wrapper">
        {/* Imagem do Estabelecimento - Sidebar */}
        <div className="image-section">
          <div className="section-title" style={{ marginBottom: 16 }}>Logo do Estabelecimento</div>
          <div className="image-preview">
            {estabelecimento.imagem_logo_preview || estabelecimento.imagem_logo ? (
              <img
                src={estabelecimento.imagem_logo_preview || buildUploadUrl(estabelecimento.imagem_logo)}
                alt="Logo"
              />
            ) : (
              <span className="no-image-text">Sem logo</span>
            )}
          </div>
          <label htmlFor="logo-sidebar-input" className="btn-upload">
            <Upload size={16} />
            Selecionar Logo
          </label>
          <input
            id="logo-sidebar-input"
            type="file"
            accept="image/*"
            className="file-input"
            onChange={(e) => handleFotoChange(e, 'imagem_logo')}
          />
          {(estabelecimento.imagem_logo_preview || estabelecimento.imagem_logo) && (
            <button
              className="btn-remove"
              type="button"
              onClick={() => removerFoto('imagem_logo')}
            >
              <Trash2 size={12} style={{ marginRight: 4 }} />
              Remover
            </button>
          )}
        </div>

        {/* Formulário - Main Content */}
        <div>
          {/* Informações Gerais */}
          <div className="form-section">
            <div className="section-title">Informações Gerais</div>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="field-label">Nome *</label>
                <input
                  type="text"
                  className={`field ${erros.nome ? 'input-error' : ''}`}
                  value={estabelecimento.nome || ""}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  placeholder="Nome do estabelecimento"
                />
                {erros.nome && <div className="error-text">{erros.nome}</div>}
                {erros.duplicado && <div className="error-text">{erros.duplicado}</div>}
              </div>
              <div className="form-group">
                <label className="field-label">E-mail</label>
                <input
                  type="email"
                  className={`field ${erros.email ? 'input-error' : ''}`}
                  value={estabelecimento.email || ""}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="seu@email.com"
                />
                {erros.email && <div className="error-text">{erros.email}</div>}
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="field-label">Tipo do estabelecimento</label>
              <select
                className="field"
                value={estabelecimento.tipo_id ? String(estabelecimento.tipo_id) : ""}
                disabled
              >
                <option value="">{loadingTipos ? "Carregando tipo..." : "Não definido"}</option>
                {tipos.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.nome ? `${t.nome}${t.descricao ? ` - ${t.descricao}` : ""}` : (t.descricao || `Tipo #${t.id}`)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-grid-2" style={{ marginTop: 12 }}>
              <div className="form-group">
                <label className="field-label">Telefone</label>
                <IMaskInput
                  className={`field ${erros.telefone ? 'input-error' : ''}`}
                  name="telefone"
                  value={estabelecimento.telefone || ""}
                  onAccept={value => handleInputChange('telefone', value)}
                  mask="(00) 00000-0000"
                  placeholder="(11) 98888-7777"
                />
                {erros.telefone && <div className="error-text">{erros.telefone}</div>}
              </div>
              <div className="form-group">
                <label className="field-label">CPF/CNPJ de cobrança *</label>
                <IMaskInput
                  className={`field ${erros.documento_faturamento ? 'input-error' : ''}`}
                  name="documento_faturamento"
                  value={estabelecimento.documento_faturamento || ""}
                  onAccept={(value) => handleInputChange('documento_faturamento', value)}
                  mask={[
                    { mask: "000.000.000-00" },
                    { mask: "00.000.000/0000-00" },
                  ]}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  inputMode="numeric"
                />
                {erros.documento_faturamento && (
                  <div className="error-text">{erros.documento_faturamento}</div>
                )}
              </div>
            </div>

            <div className="form-grid-2" style={{ marginTop: 12 }}>
              <div className="form-group">
                <label className="field-label">WhatsApp</label>
                <IMaskInput
                  className={`field ${erros.whatsapp ? 'input-error' : ''}`}
                  name="whatsapp"
                  value={estabelecimento.whatsapp || ""}
                  onAccept={value => handleInputChange('whatsapp', value)}
                  mask="(00) 00000-0000"
                  placeholder="(11) 99999-9999"
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="field-label">Descrição</label>
              <textarea
                className="field field-textarea"
                value={estabelecimento.descricao || ""}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
                placeholder="Descrição do estabelecimento..."
              />
            </div>
          </div>

          {/* Localização */}
          <div className="form-section">
            <div className="section-title">Localização</div>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="field-label">CEP</label>
                <IMaskInput
                  className={`field ${erros.cep ? 'input-error' : ''}`}
                  name="cep"
                  value={estabelecimento.cep || ""}
                  onAccept={value => handleInputChange('cep', value)}
                  mask="00000-000"
                  placeholder="00000-000"
                  onBlur={handleCepBlur}
                />
                {erros.cep && <div className="error-text">{erros.cep}</div>}
              </div>
              <div className="form-group">
                <label className="field-label">Estado</label>
                <FilaZeroSelect
                  name="estado"
                  value={estabelecimento.estado || ""}
                  options={estadoOptions}
                  onChange={(e) => handleInputChange("estado", e.target.value)}
                  placeholder="Selecione o estado"
                />
              </div>
            </div>

            <div className="form-grid-2" style={{ marginTop: 12 }}>
              <div className="form-group">
                <label className="field-label">Cidade</label>
                <input
                  type="text"
                  className={`field ${erros.duplicado ? 'input-error' : ''}`}
                  value={estabelecimento.cidade || ""}
                  onChange={(e) => handleInputChange('cidade', e.target.value)}
                  placeholder="Cidade"
                />
              </div>
              <div className="form-group">
                <label className="field-label">Endereço</label>
                <input
                  type="text"
                  className={`field ${erros.endereco ? 'input-error' : ''}`}
                  value={estabelecimento.endereco || ""}
                  onChange={(e) => handleInputChange('endereco', e.target.value)}
                  placeholder="Rua / avenida"
                />
              </div>
            </div>
            <div className="form-grid-2" style={{ marginTop: 12 }}>
              <div className="form-group">
                <label className="field-label">Número</label>
                <input
                  type="text"
                  className="field"
                  value={estabelecimento.numero || ""}
                  onChange={(e) => handleInputChange('numero', e.target.value)}
                  placeholder="Número"
                  maxLength={20}
                />
              </div>
              <div className="form-group">
                <label className="field-label">Bairro</label>
                <input
                  type="text"
                  className="field"
                  value={estabelecimento.bairro || ""}
                  readOnly
                  placeholder="Preenchido pelo CEP"
                  title="Obtido automaticamente pelo CEP (ViaCEP)"
                />
              </div>
            </div>
          </div>

          {/* Redes Sociais */}
          <div className="form-section">
            <div className="section-title">Redes Sociais e Links</div>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="field-label">Instagram</label>
                <div className="icon-input-wrapper">
                  <Instagram size={16} />
                  <input
                    type="text"
                    className="field icon-input"
                    value={estabelecimento.instagram || ""}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    placeholder="@usuario"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="field-label">Facebook</label>
                <div className="icon-input-wrapper">
                  <Facebook size={16} />
                  <input
                    type="text"
                    className="field icon-input"
                    value={estabelecimento.facebook || ""}
                    onChange={(e) => handleInputChange('facebook', e.target.value)}
                    placeholder="URL ou página"
                  />
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="field-label">Site</label>
              <div className="icon-input-wrapper">
                <Globe size={16} />
                <input
                  type="url"
                  className={`field icon-input ${erros.site ? 'input-error' : ''}`}
                  value={estabelecimento.site || ""}
                  onChange={(e) => handleInputChange('site', e.target.value)}
                  placeholder="https://www.seusite.com"
                />
              </div>
            </div>
          </div>

          {/* Horário de Funcionamento */}
          <div className="form-section">
            <div className="section-title">Horário de Funcionamento</div>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="field-label">Horário de Abertura</label>
                <FilaZeroTimeSelect
                  className={erros.horarios ? 'input-error' : ''}
                  value={estabelecimento.hora_abertura || ""}
                  onChange={(e) => handleInputChange('hora_abertura', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="field-label">Horário de Fechamento</label>
                <FilaZeroTimeSelect
                  className={erros.horarios ? 'input-error' : ''}
                  value={estabelecimento.hora_fechamento || ""}
                  onChange={(e) => handleInputChange('hora_fechamento', e.target.value)}
                />
                {erros.horarios && <div className="error-text">{erros.horarios}</div>}
              </div>
            </div>
          </div>

          {/* Imagens */}
          <div className="form-section">
            <div className="section-title">Imagens</div>

          

            {/* Fotos do Estabelecimento */}
            <label className="field-label" style={{ marginBottom: 12 }}>Fotos do Estabelecimento</label>
            <div className="form-grid-3">
              {/* Foto 1 */}
              <div>
                <div style={{
                  width: '100%',
                  height: 160,
                  background: 'var(--bg)',
                  border: '1px solid var(--line)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 10,
                  overflow: 'hidden'
                }}>
                  {estabelecimento.foto_1_preview || estabelecimento.foto_1 ? (
                    <img
                      src={estabelecimento.foto_1_preview || buildUploadUrl(estabelecimento.foto_1)}
                      alt="Foto 1"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span className="no-image-text">Foto 1</span>
                  )}
                </div>
                <label htmlFor="foto-1-input" className="btn-upload">
                  <Upload size={14} />
                  Selecionar Foto 1
                </label>
                <input
                  id="foto-1-input"
                  type="file"
                  accept="image/*"
                  className="file-input"
                  onChange={(e) => handleFotoChange(e, 'foto_1')}
                />
                {(estabelecimento.foto_1_preview || estabelecimento.foto_1) && (
                  <button
                    className="btn-remove"
                    type="button"
                    onClick={() => removerFoto('foto_1')}
                  >
                    <Trash2 size={12} style={{ marginRight: 4 }} />
                    Remover
                  </button>
                )}
              </div>

              {/* Foto 2 */}
              <div>
                <div style={{
                  width: '100%',
                  height: 160,
                  background: 'var(--bg)',
                  border: '1px solid var(--line)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 10,
                  overflow: 'hidden'
                }}>
                  {estabelecimento.foto_2_preview || estabelecimento.foto_2 ? (
                    <img
                      src={estabelecimento.foto_2_preview || buildUploadUrl(estabelecimento.foto_2)}
                      alt="Foto 2"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span className="no-image-text">Foto 2</span>
                  )}
                </div>
                <label htmlFor="foto-2-input" className="btn-upload">
                  <Upload size={14} />
                  Selecionar Foto 2
                </label>
                <input
                  id="foto-2-input"
                  type="file"
                  accept="image/*"
                  className="file-input"
                  onChange={(e) => handleFotoChange(e, 'foto_2')}
                />
                {(estabelecimento.foto_2_preview || estabelecimento.foto_2) && (
                  <button
                    className="btn-remove"
                    type="button"
                    onClick={() => removerFoto('foto_2')}
                  >
                    <Trash2 size={12} style={{ marginRight: 4 }} />
                    Remover
                  </button>
                )}
              </div>

              {/* Foto 3 (Capa) */}
              <div>
                <div style={{
                  width: '100%',
                  height: 160,
                  background: 'var(--bg)',
                  border: '1px solid var(--line)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 10,
                  overflow: 'hidden'
                }}>
                  {estabelecimento.foto_3_preview || estabelecimento.foto_3 ? (
                    <img
                      src={estabelecimento.foto_3_preview || buildUploadUrl(estabelecimento.foto_3)}
                      alt="Foto 3"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span className="no-image-text">Foto 3</span>
                  )}
                </div>
                <label htmlFor="foto-3-input" className="btn-upload">
                  <Upload size={14} />
                  Selecionar Foto 3
                </label>
                <input
                  id="foto-3-input"
                  type="file"
                  accept="image/*"
                  className="file-input"
                  onChange={(e) => handleFotoChange(e, 'foto_3')}
                />
                {(estabelecimento.foto_3_preview || estabelecimento.foto_3) && (
                  <button
                    className="btn-remove"
                    type="button"
                    onClick={() => removerFoto('foto_3')}
                  >
                    <Trash2 size={12} style={{ marginRight: 4 }} />
                    Remover
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botão Salvar Fixo */}
      <div className="save-button-wrapper estabelecimento-save-bar">
        <span className="estabelecimento-save-bar__status">
          {isEditing ? "Você tem alterações não salvas." : "Todas as alterações estão salvas."}
        </span>
        <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="save-button"
          onClick={salvar}
            disabled={saving || !isEditing}
          >
          {saving ? (
            <>
              <Spinner size={18} />
              Salvando...
            </>
          ) : (
            <>
              <Save size={18} />
              Salvar Alterações
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default Estabelecimentos;
